import { useMemo } from "react";
import type { FlightsFareSlice } from "@b2bportal/air-shopping-api";
import {
  type FareDetails,
  FlightShopCardType,
  type TripDetails,
  TrackingEventEntryPoint,
} from "@hopper-b2b/types";
import {
  useEnableFlightDetailsV2,
  useEnableHorizontalFareDetailsCard,
} from "@hopper-b2b/utilities";

import { Box } from "@material-ui/core";
import clsx from "clsx";
import type { TFunction } from "i18next";
import { Slot } from "../Slots";
import { FareDetailsCard as MiniFareDetailsCard } from "../uber/FareDetailsCard";
import { HorizontalFareDetailsCard } from "../uber/HorizontalFareDetailsCard";
import "./styles.scss";

/**
 * Flight Details Card
 * @function FlightDetailsCard
 * @param classes
 *
 */
export interface IFlightDetailsCardProps {
  className?: string;
  inExchange?: boolean;
  tripDetails: TripDetails;
  selectedFareId?: string;
  isOutgoing: boolean;
  onClick?: () => void;
  onFareClick?: (fareId: string) => void;
  onAlgomerchClick?: (label: string) => void;
  // TODO: removed for storybook
  // fareNotice?: IFareNotice[];
  fareNotice?: any;
  isMobile?: boolean;
  rewardsKey?: string;
  plusDays?: number;
  getEmptyRestrictionsText: (
    fareRating: number | undefined,
    translate: TFunction
  ) => string;
  header?: JSX.Element;
  isMixedCabinClass?: boolean;
  selectedFareClassFilters: Array<number>;
  cardType?: FlightShopCardType;
  selectedOutgoingFareSlice?: FlightsFareSlice;
  setMobileSelectedFareId?: (fareId: string) => void;
  setExpandedFlight: (expandedFlight: string) => void;
  getFlightShopFareSlice: (fareId: string) => FlightsFareSlice;
}

export const FlightDetailsCard = ({
  className,
  inExchange,
  onClick,
  tripDetails,
  selectedFareId,
  isOutgoing,
  onFareClick,
  onAlgomerchClick,
  isMobile,
  fareNotice,
  rewardsKey,
  plusDays,
  getEmptyRestrictionsText = () => "",
  header,
  isMixedCabinClass,
  selectedFareClassFilters,
  cardType,
  selectedOutgoingFareSlice,
  setMobileSelectedFareId,
  getFlightShopFareSlice,
  setExpandedFlight,
}: IFlightDetailsCardProps) => {
  const fareMultiTicketType = useMemo(
    () => tripDetails?.fareDetails[0]?.multiTicketType,
    [tripDetails]
  );

  const enableFlightDetailsV2 = useEnableFlightDetailsV2();
  const enableHorizontalFareDetailsCard = useEnableHorizontalFareDetailsCard();
  const fare = tripDetails.fareDetails.find(
    (fare) => fare.id === selectedFareId
  );
  const selectedFare = fare ? fare : tripDetails.fareDetails[0];

  const onFareDetailsClicked = (fare: FareDetails) => {
    if (onFareClick) {
      onFareClick(fare.id);
    }
  };

  const selectedFareSlice =
    selectedFare.slices[
      selectedFare.slices.findIndex((slice) =>
        isOutgoing ? slice.outgoing : !slice.outgoing
      )
    ];

  const mobileFareCardProps = {
    className: clsx("flight-fare-details", className),
    fareDetails: tripDetails.fareDetails,
    inExchange,
    selectedFare: selectedFare.id,
    isOutgoing,
    fareNotice,
    onAlgomerchClick,
    isMobile,
    rewardsKey,
    getEmptyRestrictionsText,
    selectedFareClassFilters,
    selectedOutgoingFareSlice,
    onFareSelect: onFareDetailsClicked,
    setClickedFareId: setMobileSelectedFareId,
    getFlightShopFareSlice,
    fareMultiTicketType,
  };

  return (
    <Box
      className={clsx("flight-details-card", className, {
        mini: cardType === FlightShopCardType.MINI,
        desktop: !isMobile,
        mobile: isMobile,
        "single-column-flight-details-card": enableFlightDetailsV2,
      })}
      onClick={onClick}
    >
      {/* this slot is desktop only and affects only trip selection Outbound/return */}
      {enableFlightDetailsV2 ? (
        <Slot
          id="flight-with-fare-details-summary"
          isOutgoing={isOutgoing}
          tripDetails={tripDetails}
          onFareClicked={(fare: any) => onFareDetailsClicked(fare)}
          fareSlice={selectedFareSlice}
          setExpandedFlight={setExpandedFlight}
          entryPoint={
            isOutgoing
              ? TrackingEventEntryPoint.Outbound
              : TrackingEventEntryPoint.Return
          }
        />
      ) : (
        <>
          <Box className="flight-details">
            <Slot
              id="flight-details-summary"
              className="flight-summary"
              tripDetails={tripDetails}
              entryPoint={
                isOutgoing
                  ? TrackingEventEntryPoint.Outbound
                  : TrackingEventEntryPoint.Return
              }
              isOutgoing={isOutgoing}
              showTitle={false}
              header={header}
              totalDurationMinutes={
                tripDetails.slices[isOutgoing ? 0 : 1].totalDurationMinutes
              }
              departureTime={
                isOutgoing
                  ? tripDetails.slices[0].departureTime
                  : tripDetails.slices[tripDetails.slices.length - 1]
                      .departureTime
              }
              segments={
                isOutgoing
                  ? tripDetails.slices[0].segmentDetails
                  : tripDetails.slices[tripDetails.slices.length - 1]
                      .segmentDetails
              }
              fareClass={selectedFareSlice.fareShelf?.shortBrandName || ""}
              planeInfo={
                selectedFareSlice.amenitiesUtas?.amenities?.aircraft.info
                  .displayText || ""
              }
              plusDays={plusDays}
              fareSlice={selectedFareSlice}
              isMixedCabinClass={isMixedCabinClass}
              renderAirlineIconSection={false}
            />
          </Box>
          <Box m={!isMobile ? 3 : undefined} className="fare-details-container">
            <Box className="fare-details">
              {cardType === FlightShopCardType.MINI && isMobile ? (
                enableHorizontalFareDetailsCard ? (
                  <HorizontalFareDetailsCard {...mobileFareCardProps} />
                ) : (
                  <MiniFareDetailsCard {...mobileFareCardProps} />
                )
              ) : (
                <Slot
                  id="fare-details-card"
                  className={clsx("flight-fare-details", className)}
                  fareDetails={tripDetails.fareDetails}
                  onClick={(fare: any) => onFareDetailsClicked(fare)}
                  selectedFare={selectedFare.id}
                  isOutgoing={isOutgoing}
                  fareNotice={fareNotice}
                  onAlgomerchClick={onAlgomerchClick}
                  isMobile={isMobile}
                  rewardsKey={rewardsKey}
                  getEmptyRestrictionsText={getEmptyRestrictionsText}
                  selectedFareClassFilters={selectedFareClassFilters}
                  selectedOutgoingFareSlice={selectedOutgoingFareSlice}
                  getFlightShopFareSlice={getFlightShopFareSlice}
                  tripDetails={tripDetails}
                  setExpandedFlight={setExpandedFlight}
                  fareMultiTicketType={fareMultiTicketType}
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default FlightDetailsCard;
